import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import { differenceInCalendarDays } from 'date-fns';
import {
  Alert,
  alpha,
  Box,
  Button,
  ButtonBase,
  Collapse,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArchiveOutlined as ArchiveIcon,
  AssignmentTurnedInOutlined as CheckInIcon,
  CalendarMonthOutlined as LeaveIcon,
  Check as CheckIcon,
  CheckCircleOutline as ApprovedIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  EditCalendarOutlined as LeaveChangeIcon,
  EmojiEventsOutlined as NominationIcon,
  EventBusyOutlined as LeaveCancelledIcon,
  ExpandMore as ExpandMoreIcon,
  HighlightOff as DeniedIcon,
  TaskAlt as AllClearIcon,
  WifiOff as OfflineIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../features/auth/useAuth';
import { useNotifications, Notification } from '../../../contexts/NotificationContext';
import { DECISION_UNDO_MS, QueueItem, useDecisionQueue } from '../../../contexts/DecisionQueueContext';
import {
  describeUpdate,
  foldSubtitle,
  foldTitle,
  formatDayCount,
  formatLeaveRange,
  groupUpdates,
  isUpdateNotification,
  sinceLabel,
  timeLabel,
  UpdateView,
} from './notificationPresentation';

type Tone = 'warning' | 'secondary' | 'slate' | 'success' | 'error';

const drain = keyframes`from { transform: scaleX(1); } to { transform: scaleX(0); }`;

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

const useOnlineStatus = () => {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
};

const TypeTile: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => {
  const theme = useTheme();
  const colours: Record<Tone, [string, string]> = {
    warning: [theme.palette.warning.dark, alpha(theme.palette.warning.main, 0.14)],
    secondary: [theme.palette.secondary.main, alpha(theme.palette.secondary.main, 0.1)],
    slate: [theme.palette.grey[600], theme.palette.grey[100]],
    success: [theme.palette.success.dark, alpha(theme.palette.success.main, 0.12)],
    error: [theme.palette.error.dark, alpha(theme.palette.error.main, 0.1)],
  };
  const [fg, bg] = colours[tone];
  return (
    <Box
      aria-hidden
      sx={{
        width: 32,
        height: 32,
        borderRadius: '8px',
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
        color: fg,
        bgcolor: bg,
        fontWeight: 700,
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
        '& svg': { fontSize: 18 },
      }}
    >
      {children}
    </Box>
  );
};

const UpdateTile: React.FC<{ update: Pick<UpdateView, 'kind' | 'npsRating' | 'leaveOutcome'> }> = ({ update }) => {
  switch (update.kind) {
    case 'checkin':
      return <TypeTile tone="slate"><CheckInIcon /></TypeTile>;
    case 'nps': {
      const score = update.npsRating;
      const tone: Tone = score === undefined ? 'slate' : score <= 2 ? 'error' : score === 3 ? 'warning' : 'success';
      return <TypeTile tone={tone}>{score ?? '–'}</TypeTile>;
    }
    case 'leave':
      if (update.leaveOutcome === 'approved') return <TypeTile tone="success"><ApprovedIcon /></TypeTile>;
      if (update.leaveOutcome === 'denied') return <TypeTile tone="error"><DeniedIcon /></TypeTile>;
      return <TypeTile tone="slate"><LeaveCancelledIcon /></TypeTile>;
    case 'nomination':
      return <TypeTile tone="secondary"><NominationIcon /></TypeTile>;
    default:
      return <TypeTile tone="slate"><CheckInIcon /></TypeTile>;
  }
};

const SectionHeader: React.FC<{ title: string; count?: React.ReactNode; action?: React.ReactNode }> = ({
  title,
  count,
  action,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 0.75 }}>
    <Typography component="h3" sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>
      {title}
    </Typography>
    {count}
    <Box sx={{ flex: 1 }} />
    {action}
  </Box>
);

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const CountPill: React.FC<{ value: number; tone: 'urgent' | 'soft'; label: string }> = ({ value, tone, label }) => (
  <>
    <Box component="span" sx={visuallyHidden}>{label}</Box>
    <CountPillBadge value={value} tone={tone} />
  </>
);

const CountPillBadge: React.FC<{ value: number; tone: 'urgent' | 'soft' }> = ({ value, tone }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      minWidth: 20,
      height: 20,
      px: 0.75,
      borderRadius: 10,
      display: 'inline-grid',
      placeItems: 'center',
      fontSize: 11.5,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      bgcolor: (theme) => (tone === 'urgent' ? theme.palette.error.main : alpha(theme.palette.primary.main, 0.1)),
      color: tone === 'urgent' ? 'common.white' : 'primary.dark',
    }}
  >
    {tone === 'urgent' ? value : `${value} new`}
  </Box>
);

const rowGrid = {
  display: 'grid',
  gridTemplateColumns: '32px minmax(0, 1fr) auto',
  columnGap: 1.5,
} as const;

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

const compactButton = { py: 0.5, px: 1.25, minHeight: 30, fontSize: 13 } as const;

/* ---------------------------- Waiting on you ---------------------------- */

/** What a held decision says, in the row and to screen readers. */
const describeDecision = (item: Extract<QueueItem, { kind: 'leave' }>) => {
  const decision = item.decision;
  if (!decision) return null;
  const first = firstName(item.request.staff?.displayName || 'A team member');
  const isChange = item.request.status === 'CHANGE_PENDING' || Boolean(item.request.change?.isChangeRequest);
  const approved = decision.verb === 'APPROVED';
  return {
    title: `${approved ? 'Approved' : 'Denied'} ${first}’s ${isChange ? 'new dates' : 'leave'}.`,
    detail: !approved && decision.comment ? `${first} will see your reason.` : `${first} will be notified.`,
  };
};

const UndoRow: React.FC<{
  title: string;
  detail: string;
  heldAt: number;
  onUndo: () => void;
  undoRef?: React.Ref<HTMLButtonElement>;
}> = ({ title, detail, heldAt, onUndo, undoRef }) => {
  // Reopening the panel mid-window resumes the bar where the real timer is.
  // Fixed at mount: a running animation would jump if its delay changed on re-render.
  const [elapsed] = useState(() => Math.min(DECISION_UNDO_MS, Math.max(0, Date.now() - heldAt)));
  return (
    <Box
      sx={{
        ...rowGrid,
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        px: 2,
        py: 1.25,
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
      }}
    >
      <TypeTile tone="success"><CheckIcon /></TypeTile>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'success.dark', ...ellipsis }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...ellipsis }}>{detail}</Typography>
      </Box>
      <Button ref={undoRef} size="small" onClick={onUndo} sx={compactButton}>
        Undo
      </Button>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 2,
          width: '100%',
          bgcolor: 'success.main',
          transformOrigin: 'left center',
          animation: `${drain} ${DECISION_UNDO_MS}ms linear forwards`,
          animationDelay: `${-elapsed}ms`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  );
};

const LeaveQueueRow: React.FC<{
  item: Extract<QueueItem, { kind: 'leave' }>;
  now: Date;
  offline: boolean;
  showLocation: boolean;
  onOpen: (href: string) => void;
}> = ({ item, now, offline, showLocation, onOpen }) => {
  const { approveLeave, denyLeave, undoDecision } = useDecisionQueue();
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');
  // The button that was used unmounts as the row swaps between its two states,
  // so focus follows to the matching button in the other one. Only after a
  // decision or Undo made here: a panel reopened mid-window leaves focus alone.
  const refocus = useRef<'undo' | 'approve' | null>(null);
  const undoRef = useRef<HTMLButtonElement>(null);
  const approveRef = useRef<HTMLButtonElement>(null);
  const request = item.request;
  const name = request.staff?.displayName || 'A team member';
  const first = firstName(name);
  const isChange = request.status === 'CHANGE_PENDING' || Boolean(request.change?.isChangeRequest);

  useEffect(() => {
    const target = item.decision ? 'undo' : 'approve';
    if (refocus.current !== target) return;
    refocus.current = null;
    (target === 'undo' ? undoRef : approveRef).current?.focus();
  }, [item.decision]);

  const held = describeDecision(item);
  if (item.decision && held) {
    return (
      <UndoRow
        title={held.title}
        detail={held.detail}
        heldAt={item.decision.heldAt}
        undoRef={undoRef}
        onUndo={() => {
          refocus.current = 'approve';
          undoDecision(request.id);
        }}
      />
    );
  }

  const start = request.period?.startDate?.toDate?.();
  const end = request.period?.endDate?.toDate?.();
  const requestedAt = request.timeline?.createdAt?.toDate?.();
  const dates = start && end ? formatLeaveRange(start, end) : 'Dates not set';
  const location = showLocation ? request.visibility?.staffLocation : null;
  // Still needs a decision, but shouldn't read as upcoming leave.
  const passed = end ? differenceInCalendarDays(now, end) > 0 : false;
  const detail = [
    isChange ? `Now ${dates}` : dates,
    formatDayCount(request.period?.totalDays ?? 0),
    passed ? 'Dates have passed' : null,
    location,
  ]
    .filter(Boolean)
    .join(' · ');
  const staffNote = request.comments?.staff;
  const offlineHint = offline ? 'You’re offline' : '';

  return (
    <Box sx={{ ...rowGrid, px: 2, py: 1.25 }}>
      <TypeTile tone="warning">{isChange ? <LeaveChangeIcon /> : <LeaveIcon />}</TypeTile>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, ...ellipsis }}>
          {name}{' '}
          <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            · {isChange ? 'Change of dates' : request.reason || 'Leave'}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...ellipsis }}>{detail}</Typography>
        {staffNote && (
          <Typography sx={{ fontSize: 12, color: 'grey.500', ...ellipsis }}>“{staffNote}”</Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: 'grey.500', whiteSpace: 'nowrap', pt: 0.25 }}>
        {requestedAt ? sinceLabel(requestedAt, now) : ''}
      </Typography>

      {denying ? (
        <Box
          component="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            refocus.current = 'undo';
            denyLeave(request, reason);
            setDenying(false);
          }}
          sx={{ gridColumn: '2 / -1', display: 'grid', gap: 1, mt: 1.25 }}
        >
          <TextField
            autoFocus
            multiline
            minRows={2}
            size="small"
            label="Reason"
            placeholder="For example, two coaches are already off that day"
            helperText={`Optional. ${first} will see it.`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              // Escape cancels the form rather than closing the whole panel and losing the reason.
              if (event.key === 'Escape') {
                event.stopPropagation();
                setDenying(false);
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button type="submit" size="small" variant="contained" color="error" disabled={offline} sx={compactButton}>
              Deny request
            </Button>
            <Button size="small" color="inherit" onClick={() => setDenying(false)} sx={compactButton}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ gridColumn: '2 / -1', display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
          <Tooltip title={offlineHint}>
            <span>
              <Button
                ref={approveRef}
                size="small"
                variant="contained"
                disableElevation
                startIcon={<CheckIcon />}
                disabled={offline}
                onClick={() => {
                  refocus.current = 'undo';
                  approveLeave(request);
                }}
                sx={compactButton}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={offlineHint}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={offline}
                onClick={() => setDenying(true)}
                sx={{ ...compactButton, borderColor: 'divider' }}
              >
                Deny…
              </Button>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => onOpen('/leave-manager')} sx={compactButton}>
            Details
          </Button>
        </Box>
      )}
    </Box>
  );
};

const NominationQueueRow: React.FC<{
  notification: Notification;
  now: Date;
  showLocation: boolean;
  onOpen: (href: string, notificationId: string) => void;
}> = ({ notification, now, showLocation, onOpen }) => {
  const meta = notification.metadata || {};
  const client = notification.client?.name || notification.clientName || 'A client';
  const detail = [
    meta.sessionsCompleted !== undefined ? `${meta.sessionsCompleted} sessions completed` : null,
    showLocation ? meta.clientLocation : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const href = meta.nominationId ? `/success-nominations?highlight=${meta.nominationId}` : '/success-nominations';
  const created = notification.timestamp?.toDate?.();

  return (
    <Box sx={{ ...rowGrid, px: 2, py: 1.25 }}>
      <TypeTile tone="secondary"><NominationIcon /></TypeTile>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, ...ellipsis }}>
          {client}{' '}
          <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            · nominated by {meta.submittedByName || 'a team member'}
          </Box>
        </Typography>
        {detail && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...ellipsis }}>{detail}</Typography>}
        {meta.excerpt && (
          <Typography sx={{ fontSize: 12, color: 'grey.500', ...ellipsis }}>“{meta.excerpt}”</Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: 'grey.500', whiteSpace: 'nowrap', pt: 0.25 }}>
        {created ? sinceLabel(created, now) : ''}
      </Typography>
      <Box sx={{ gridColumn: '2 / -1', mt: 1.25 }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          endIcon={<ChevronRightIcon />}
          onClick={() => onOpen(href, notification.id)}
          sx={{ ...compactButton, borderColor: 'divider' }}
        >
          Review nomination
        </Button>
      </Box>
    </Box>
  );
};

/* -------------------------------- Updates ------------------------------- */

const UpdateRow: React.FC<{
  update: UpdateView;
  groupLabel: string;
  indent?: boolean;
  onOpen: (href: string, notificationId: string) => void;
  onArchive: (notificationId: string) => void;
}> = ({ update, groupLabel, indent, onOpen, onArchive }) => (
  <Box
    sx={{
      ...rowGrid,
      alignItems: 'center',
      position: 'relative',
      pl: indent ? 3.5 : 2,
      pr: 1,
      '&:hover, &:focus-within': { bgcolor: 'action.hover' },
      // With a mouse, Archive replaces the time on hover or focus. Touch screens
      // of any size have no hover, so there it is always shown.
      '@media (hover: hover) and (pointer: fine)': {
        '& .update-archive': { display: 'none' },
        '&:hover .update-archive, &:focus-within .update-archive': { display: 'inline-flex' },
        '&:hover .update-time, &:focus-within .update-time': { display: 'none' },
      },
    }}
  >
    <UpdateTile update={update} />
    <ButtonBase
      onClick={() => onOpen(update.href, update.id)}
      sx={{
        display: 'block',
        position: 'static',
        textAlign: 'left',
        minWidth: 0,
        py: 0.875,
        // Stretch the click target over the whole row except the archive button.
        '&::before': { content: '""', position: 'absolute', inset: 0, right: 44 },
      }}
    >
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: update.read ? 500 : 600,
          color: update.read ? 'text.secondary' : 'text.primary',
          ...ellipsis,
        }}
      >
        {!update.read && <Box component="span" sx={visuallyHidden}>Unread: </Box>}
        {update.title}
      </Typography>
      {update.subtitle && (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...ellipsis }}>{update.subtitle}</Typography>
      )}
    </ButtonBase>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 44, justifyContent: 'flex-end' }}>
      {!update.read && (
        <Box
          className="update-time"
          component="span"
          aria-hidden
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flex: 'none' }}
        />
      )}
      <Typography className="update-time" component="span" sx={{ fontSize: 11.5, color: 'grey.500', whiteSpace: 'nowrap' }}>
        {timeLabel(update.date, groupLabel)}
      </Typography>
      <IconButton
        className="update-archive"
        size="small"
        aria-label={`Archive “${update.title}”`}
        onClick={() => onArchive(update.id)}
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <ArchiveIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);

const FoldRow: React.FC<{
  items: UpdateView[];
  kind: UpdateView['kind'];
  groupLabel: string;
  onOpen: (href: string, notificationId: string) => void;
  onArchive: (notificationId: string) => void;
  onMarkRead: (notificationIds: string[]) => void;
}> = ({ items, kind, groupLabel, onOpen, onArchive, onMarkRead }) => {
  const [expanded, setExpanded] = useState(false);
  const unreadIds = items.filter((item) => !item.read).map((item) => item.id);

  return (
    <>
      <ButtonBase
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        sx={{
          ...rowGrid,
          alignItems: 'center',
          width: '100%',
          textAlign: 'left',
          pl: 2,
          pr: 1.5,
          py: 0.875,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <UpdateTile update={{ kind }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: unreadIds.length ? 600 : 500, ...ellipsis }}>
            {foldTitle(kind, items.length)}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...ellipsis }}>{foldSubtitle(items)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'grey.500' }}>
          {unreadIds.length > 0 && (
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main' }}>
              <Box component="span" sx={visuallyHidden}>{unreadIds.length} unread</Box>
            </Box>
          )}
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </Box>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ bgcolor: 'grey.50', borderBlock: 1, borderColor: 'divider', pb: 0.5 }}>
          {items.map((item) => (
            <UpdateRow
              key={item.id}
              update={item}
              groupLabel={groupLabel}
              indent
              onOpen={onOpen}
              onArchive={onArchive}
            />
          ))}
          {unreadIds.length > 0 && (
            <Button size="small" onClick={() => onMarkRead(unreadIds)} sx={{ ...compactButton, ml: 2.5, mt: 0.5 }}>
              Mark these read
            </Button>
          )}
        </Box>
      </Collapse>
    </>
  );
};

const SkeletonRows: React.FC<{ count: number; withActions?: boolean }> = ({ count, withActions }) => (
  <Box aria-hidden>
    {Array.from({ length: count }, (_, index) => (
      <Box key={index} sx={{ ...rowGrid, px: 2, py: 1 }}>
        <Skeleton variant="rounded" width={32} height={32} />
        <Box>
          <Skeleton width={`${[64, 52, 70, 58][index % 4]}%`} />
          <Skeleton width={`${[42, 48, 36, 44][index % 4]}%`} />
          {withActions && <Skeleton variant="rounded" width={160} height={28} sx={{ mt: 0.75 }} />}
        </Box>
      </Box>
    ))}
  </Box>
);

const QuietNote: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', px: 2, pt: 0.5, pb: 1.5, color: 'text.secondary' }}>
    {icon}
    <Typography sx={{ fontSize: 13 }}>{children}</Typography>
  </Box>
);

/* --------------------------------- Panel -------------------------------- */

interface NotificationPanelProps {
  onClose: () => void;
  /** Phone layout, which adds a close button. */
  mobile?: boolean;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose, mobile = false }) => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const {
    notifications,
    status,
    retry,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    undoArchive,
    pendingArchives,
  } = useNotifications();
  const queue = useDecisionQueue();
  const online = useOnlineStatus();
  // The panel mounts when opened, so "today" is fixed for the time it is open.
  const [now] = useState(() => new Date());

  const updates = useMemo(
    () => notifications.filter(isUpdateNotification).map((notification) => describeUpdate(notification, user?.uid)),
    [notifications, user?.uid]
  );
  const groups = useMemo(() => groupUpdates(updates, now), [updates, now]);
  const unreadUpdateIds = updates.filter((update) => !update.read).map((update) => update.id);
  const lastArchived = pendingArchives[pendingArchives.length - 1];

  // One live region, mounted with the panel, so screen readers hear each
  // decision and archive (a status row inserted with its text is often missed).
  const latestDecision = queue.items
    .filter((item): item is Extract<QueueItem, { kind: 'leave' }> => item.kind === 'leave' && Boolean(item.decision))
    .sort((a, b) => (b.decision?.heldAt ?? 0) - (a.decision?.heldAt ?? 0))[0];
  const announcement = [
    latestDecision ? describeDecision(latestDecision)?.title : null,
    lastArchived ? `Archived “${describeUpdate(lastArchived, user?.uid).title}”.` : null,
  ]
    .filter(Boolean)
    .join(' ');
  const showLocation = userRole === 'ADMIN';

  // Navigate first; the read receipt is written in the background.
  const open = (href: string, notificationId?: string) => {
    onClose();
    navigate(href);
    if (notificationId) void markAsRead(notificationId);
  };

  const failed = status === 'error' || queue.leaveStatus === 'error';
  const showQueue = queue.canDecideLeave || queue.items.length > 0;
  const queueLoading = queue.canDecideLeave && queue.leaveStatus === 'loading';
  const feedLoading = status === 'loading' && notifications.length === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {announcement ? `${announcement} Undo is available for a few seconds.` : ''}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 2,
          pr: 1,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flex: 'none',
        }}
      >
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', flex: 1 }}>
          Notifications
        </Typography>
        {mobile && (
          <IconButton aria-label="Close notifications" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {!online && (
          <Alert severity="info" icon={<OfflineIcon fontSize="inherit" />} sx={{ m: 1.5, mb: 0.5, py: 0.25 }}>
            You’re offline. Showing what was loaded earlier; decisions are paused until you reconnect.
          </Alert>
        )}
        {online && failed && (
          <Alert
            severity="warning"
            sx={{ m: 1.5, mb: 0.5, py: 0.25 }}
            action={
              <Button
                color="inherit"
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => {
                  retry();
                  queue.retry();
                }}
              >
                Try again
              </Button>
            }
          >
            Couldn’t load the latest notifications. What’s shown may be out of date.
          </Alert>
        )}

        {showQueue && (
          <Box component="section" aria-label="Waiting on you" sx={{ pb: 0.5 }}>
            <SectionHeader
              title="Waiting on you"
              count={
                queue.waitingCount > 0 ? (
                  <CountPill value={queue.waitingCount} tone="urgent" label={`${queue.waitingCount} waiting`} />
                ) : undefined
              }
            />
            {queueLoading && !queue.items.some((item) => item.kind === 'leave') ? (
              <SkeletonRows count={2} withActions />
            ) : queue.items.length === 0 ? (
              <QuietNote icon={<AllClearIcon fontSize="small" sx={{ color: 'success.main', mt: '1px' }} />}>
                Nothing waiting on you. Leave requests and success nominations will appear here.
              </QuietNote>
            ) : (
              <Box sx={{ '& > * + *': { borderTop: 1, borderColor: 'divider' } }}>
              {queue.items.map((item) =>
                item.kind === 'leave' ? (
                  <LeaveQueueRow
                    key={item.id}
                    item={item}
                    now={now}
                    offline={!online}
                    showLocation={showLocation}
                    onOpen={(href) => open(href)}
                  />
                ) : (
                  <NominationQueueRow
                    key={item.id}
                    notification={item.notification}
                    now={now}
                    showLocation={showLocation}
                    onOpen={open}
                  />
                )
              )}
              </Box>
            )}
          </Box>
        )}

        <Box
          component="section"
          aria-label="Updates"
          sx={{ pb: 1, borderTop: showQueue ? 1 : 0, borderColor: 'divider' }}
        >
          <SectionHeader
            title="Updates"
            count={
              unreadUpdateIds.length > 0 ? (
                <CountPill value={unreadUpdateIds.length} tone="soft" label={`${unreadUpdateIds.length} new`} />
              ) : undefined
            }
          />
          {lastArchived && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mx: 1.5,
                mb: 0.5,
                pl: 1.25,
                pr: 0.5,
                py: 0.25,
                borderRadius: '8px',
                bgcolor: 'grey.100',
                color: 'grey.700',
              }}
            >
              <ArchiveIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 12.5, flex: 1, minWidth: 0, ...ellipsis }}>
                Archived “{describeUpdate(lastArchived, user?.uid).title}”
              </Typography>
              <Button size="small" onClick={() => undoArchive(lastArchived.id)} sx={compactButton}>
                Undo
              </Button>
            </Box>
          )}
          {feedLoading ? (
            <SkeletonRows count={3} />
          ) : groups.length === 0 ? (
            <QuietNote>No updates yet. Check-ins, NPS ratings and leave decisions will appear here.</QuietNote>
          ) : (
            groups.map((group) => (
              <Box key={group.label}>
                <Typography sx={{ px: 2, pt: 1, pb: 0.25, fontSize: 11.5, fontWeight: 600, color: 'grey.500' }}>
                  {group.label}
                </Typography>
                {group.entries.map((entry) =>
                  entry.type === 'item' ? (
                    <UpdateRow
                      key={entry.item.id}
                      update={entry.item}
                      groupLabel={group.label}
                      onOpen={open}
                      onArchive={archiveNotification}
                    />
                  ) : (
                    <FoldRow
                      key={entry.key}
                      items={entry.items}
                      kind={entry.kind}
                      groupLabel={group.label}
                      onOpen={open}
                      onArchive={archiveNotification}
                      onMarkRead={(ids) => void markAllAsRead(ids)}
                    />
                  )
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1, py: 0.75, borderTop: 1, borderColor: 'divider', flex: 'none' }}>
        <Button
          size="small"
          disabled={unreadUpdateIds.length === 0}
          onClick={() => void markAllAsRead(unreadUpdateIds)}
          sx={compactButton}
        >
          Mark updates read
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationPanel;
